/**
 * The least-privilege permission manifest (docs/event-model.md §8,
 * ADR-0014): what you would have to grant for the script to work as
 * observed. Pure builders over the analysis input.
 *
 * Per ADR-0016 the browser-specific formatters (CSP, Permissions-Policy) live
 * in core/environments/browser/manifest.ts; the Deno-style flags and the
 * plain-English summary are built here.
 */
import type { DataFlow } from "../canary";
import type { SimContext } from "../context";
import {
  cspFor,
  formatCsp,
  formatPermissionsPolicy,
  permissionsFor,
} from "../environments/browser/manifest";
import { type WATCHED_GLOBALS, watcherFamily } from "../environments/browser/watchers";
import type { AuditEvent, Layer } from "../events";
import { formatVirtualTime } from "../timeline";
import { originOf, parseUrl } from "../url";
import { hostOf, isThirdParty } from "../url/party";

export { formatCsp, formatPermissionsPolicy };

/** What the manifest builders read. */
export interface ManifestInput {
  /** The event log, with severities as escalated by analysis. */
  events: readonly AuditEvent[];
  layers: readonly Layer[];
  flows: readonly DataFlow[];
  context: SimContext;
}

export interface Manifest {
  /**
   * Content-Security-Policy directives → sources, e.g.
   * `{ "connect-src": ["https://px.example"], "script-src": ["'unsafe-eval'"] }`.
   * Directives the script doesn't need are left out.
   */
  csp: Record<string, string[]>;
  /** Permissions-Policy features touched, e.g. `["geolocation", "clipboard-read"]`. */
  permissions: string[];
  /** Deno-style flags, e.g. `["--allow-net=api.example,cdn.example"]`. */
  flags: string[];
  /** The plain-English summary, one sentence per entry. */
  summary: string[];
}

/** CSP directives the script needs. */
export function cspDirectives(input: ManifestInput): Record<string, string[]> {
  return cspFor(input.events, input.context.url);
}

/** Permissions-Policy features the script touched. */
export function permissionsPolicy(input: ManifestInput): string[] {
  return permissionsFor(input.events);
}

/** Every URL the script made the browser fetch: requests, resource loads, navigations, imports. */
function contactedUrls(events: readonly AuditEvent[]): string[] {
  const urls: string[] = [];
  for (const e of events) {
    if (e.network) urls.push(e.network.url);
    const moduleUrl = e.codegen?.module?.url;
    if (e.op === "codeload" && moduleUrl) urls.push(moduleUrl);
  }
  return urls;
}

/** `host` or `host:port`, as Deno's `--allow-net` takes them. */
function netHost(url: string): string | null {
  const parts = parseUrl(url);
  if (!parts || parts.hostname === "") return null;
  return parts.port ? `${parts.hostname}:${parts.port}` : parts.hostname;
}

const unique = <T>(items: Iterable<T>): T[] => [...new Set(items)];

/** Deno-style permission flags. */
export function denoFlags(input: ManifestInput): string[] {
  const hosts = unique(
    contactedUrls(input.events)
      .map(netHost)
      .filter((h): h is string => h !== null),
  );
  return hosts.length > 0 ? [`--allow-net=${hosts.join(",")}`] : [];
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const listOf = (items: readonly string[], max = 6) =>
  items.length <= max
    ? items.join(", ")
    : `${items.slice(0, max).join(", ")}, and ${items.length - max} more`;
const sentence = (clauses: readonly string[]) =>
  clauses.length <= 1
    ? (clauses[0] ?? "")
    : `${clauses.slice(0, -1).join(", ")} and ${clauses[clauses.length - 1]}`;

const FAMILY_NOUN: Record<keyof typeof WATCHED_GLOBALS, [string, string]> = {
  analytics: ["analytics tool", "analytics tools"],
  security: ["security tool", "security tools"],
  wallet: ["crypto wallet", "crypto wallets"],
};

function networkSentences({ events, flows, context }: ManifestInput): string[] {
  const urls = contactedUrls(events);
  if (urls.length === 0) return [];
  const third = unique(
    urls
      .filter((u) => isThirdParty(u, context.url) === true)
      .map(hostOf)
      .filter((h): h is string => h !== null),
  );
  const out = [
    third.length === 0
      ? "This script only contacts its own site."
      : `This script contacts ${plural(third.length, "third-party domain")}: ${listOf(third)}.`,
  ];
  for (const flow of flows) {
    const to = flow.origin ? (hostOf(flow.origin) ?? flow.origin) : flow.sink;
    const how = flow.encodings.length > 0 ? ` (${flow.encodings.join(", ")})` : "";
    out.push(`It sends ${flow.source} to ${to}${how}.`);
  }
  return out;
}

function storageSentence({ events }: ManifestInput): string[] {
  const clauses: string[] = [];
  if (events.some((e) => e.tags.includes("cookie-read"))) clauses.push("reads cookies");
  if (
    events.some(
      (e) =>
        e.category === "storage" &&
        !e.tags.includes("storage-write") &&
        /^(localStorage|sessionStorage|indexedDB|IDB|caches|Cache)/.test(e.target),
    )
  ) {
    clauses.push("reads browser storage");
  }
  if (events.some((e) => e.tags.includes("storage-write"))) clauses.push("writes browser storage");
  if (events.some((e) => e.tags.includes("form-read"))) clauses.push("reads form fields");
  return clauses.length > 0 ? [`It ${sentence(clauses)}.`] : [];
}

function codeSentence({ events, layers, context }: ManifestInput): string[] {
  const clauses: string[] = [];
  if (layers.length > 0) {
    clauses.push(`executes ${plural(layers.length, "layer")} of dynamically generated code`);
  }
  const scriptOrigins = unique(
    events
      .map((e) =>
        e.network && e.target === "HTMLScriptElement.src"
          ? e.network.url
          : e.op === "codeload"
            ? e.codegen?.module?.url
            : undefined,
      )
      .filter((u): u is string => u !== undefined && isThirdParty(u, context.url) === true)
      .map(originOf),
  );
  if (scriptOrigins.length > 0) {
    clauses.push(`loads script from ${plural(scriptOrigins.length, "other site")}`);
  }
  return clauses.length > 0 ? [`It ${sentence(clauses)}.`] : [];
}

function probeSentence({ events }: ManifestInput): string[] {
  const names = unique(
    events
      .filter((e) => e.category === "probe" && e.target.startsWith("window."))
      .map((e) => e.target.slice("window.".length).split(".")[0] ?? ""),
  );
  if (names.length === 0) return [];
  const families = (Object.keys(FAMILY_NOUN) as (keyof typeof FAMILY_NOUN)[])
    .map((family) => {
      const n = names.filter((name) => watcherFamily(name) === family).length;
      const [one, many] = FAMILY_NOUN[family];
      return n > 0 ? plural(n, one, many) : null;
    })
    .filter((s): s is string => s !== null);
  const including = families.length > 0 ? `, including ${sentence(families)}` : "";
  return [
    `It looks for ${plural(names.length, "global")} that ${names.length === 1 ? "isn't" : "aren't"} its own${including}.`,
  ];
}

function fingerprintSentence({ events }: ManifestInput): string[] {
  const surfaces = unique(events.filter((e) => e.category === "fingerprint").map((e) => e.target));
  return surfaces.length > 0
    ? [`It reads ${plural(surfaces.length, "fingerprinting surface")}.`]
    : [];
}

const HOUR = 3_600_000;

function timerSentence({ events }: ManifestInput): string[] {
  const longest = events.reduce(
    (max, e) => (e.op === "call" && e.timer ? Math.max(max, e.timer.delay) : max),
    0,
  );
  return longest > HOUR ? [`It schedules code to run ${formatVirtualTime(longest)} later.`] : [];
}

function permissionSentence(input: ManifestInput): string[] {
  const features = permissionsPolicy(input);
  return features.length > 0
    ? [`It asks for permission-gated features: ${features.join(", ")}.`]
    : [];
}

/** The plain-English summary: what the script does, one sentence per theme. */
export function englishSummary(input: ManifestInput): string[] {
  return [
    ...networkSentences(input),
    ...storageSentence(input),
    ...codeSentence(input),
    ...probeSentence(input),
    ...fingerprintSentence(input),
    ...timerSentence(input),
    ...permissionSentence(input),
  ];
}

export function buildManifest(input: ManifestInput): Manifest {
  return {
    csp: cspDirectives(input),
    permissions: permissionsPolicy(input),
    flags: denoFlags(input),
    summary: englishSummary(input),
  };
}
